import { convertEncodedContentToNoteContent } from "@/modules/notes/conversions";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import type { AppStore } from "@/store/AppStore";
import type { INoteObservable, NotesIndexTuple } from "@/store/note";
import {
  AiToolkitNoteContentNotFoundError,
  AiToolkitNoteNotFoundError,
} from "@/modules/ai-toolkit/errors";
import type {
  AiToolkitLocalNote,
  AiToolkitLocalNoteContent,
} from "@mem-client/ai-toolkit/contract";

export const DEFAULT_CONTENT_CHAR_LIMIT = 4_000;
export const DEFAULT_PREVIEW_CHAR_LIMIT = 500;

export const getNoteIdFromIndexTuple = (indexTuple: NotesIndexTuple): string =>
  indexTuple[indexTuple.length - 1] as string;

export const isLocalNote = (note: AiToolkitLocalNote | null): note is AiToolkitLocalNote =>
  note !== null;

export const assertNoteIsAvailable = ({
  note,
  noteId,
}: {
  note: INoteObservable | null | undefined;
  noteId: string;
}): INoteObservable => {
  if (!note?.isAvailable) {
    throw new AiToolkitNoteNotFoundError({ noteId });
  }

  return note;
};

const truncateText = ({ text, maxChars }: { text: string; maxChars: number }): string => {
  if (text.length <= maxChars) {
    return text;
  }

  return `${text.slice(0, maxChars)}...`;
};

const readLocalNoteContent = async ({
  store,
  note,
  includeContent,
  maxContentChars,
  missingContentBehavior,
}: {
  store: AppStore;
  note: INoteObservable;
  includeContent: "none" | "plaintext" | "mdx";
  maxContentChars: number;
  missingContentBehavior: "return_status" | "throw";
}): Promise<AiToolkitLocalNoteContent> => {
  if (includeContent === "none") {
    return { status: "not_requested" as const };
  }

  const noteContentDocument = await store.noteContentDocuments.getAsync(
    resolveNoteContentDocumentSyncModelUuid({ noteId: note.id })
  );
  const encodedContent = noteContentDocument?.remoteContent;

  if (!encodedContent) {
    if (missingContentBehavior === "throw") {
      throw new AiToolkitNoteContentNotFoundError({ noteId: note.id });
    }

    return { status: "not_found" as const };
  }

  const extractedContent = convertEncodedContentToNoteContent(encodedContent);

  if (includeContent === "mdx") {
    return {
      status: "local" as const,
      mdx: truncateText({ text: extractedContent.mdx, maxChars: maxContentChars }),
    };
  }

  return {
    status: "local" as const,
    plaintext: truncateText({ text: extractedContent.plaintext, maxChars: maxContentChars }),
  };
};

export const buildLocalNote = async ({
  store,
  note,
  includeContent,
  maxContentChars,
  missingContentBehavior,
}: {
  store: AppStore;
  note: INoteObservable;
  includeContent: "none" | "plaintext" | "mdx";
  maxContentChars: number;
  missingContentBehavior: "return_status" | "throw";
}): Promise<AiToolkitLocalNote> => ({
  id: note.id,
  title: note.title,
  secondaryTitle: note.secondaryTitle,
  path: note.path,
  createdAt: note.createdAt,
  modifiedAt: note.modifiedAt,
  locallyCreatedAt: note.locallyCreatedAt,
  isOwnedByMe: note.isOwnedByMe,
  isShared: note.isShared,
  content: await readLocalNoteContent({
    store,
    note,
    includeContent,
    maxContentChars,
    missingContentBehavior,
  }),
});
